// Wiring shared by main.tsx and the tests: one QueryClient, one router, and the rule that a 401
// anywhere in the app (session ended) drops cached data and returns to /login.
import type { IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { RouterProvider, type RouterHistory } from '@tanstack/react-router';
import { ME_QUERY_KEY } from './api/me.ts';
import { createQueryClient } from './api/query-client.ts';
import type { EntraSettings } from './auth/msal.ts';
import { createAppRouter } from './router.tsx';
import { ErrorBoundary } from './ui/error-boundary.tsx';

export interface AppRuntime {
  readonly queryClient: QueryClient;
  readonly router: ReturnType<typeof createAppRouter>;
}

export interface AppRuntimeOptions {
  readonly entra: EntraSettings | null;
  readonly history?: RouterHistory;
  /** Query retries for network and 5xx failures; tests pass 0. */
  readonly retries?: number;
}

export function createAppRuntime(opts: AppRuntimeOptions): AppRuntime {
  // Only called after a query fails, by which time `router` below is initialised.
  // Already on /login: nothing to leave, but cached data of the ended session is still dropped.
  // When this tab had a signed-in person (/me cached), the login page says the session ended
  // (review D-R19); a first visit with no cookie lands on /login with no message.
  const leave = async (): Promise<void> => {
    const ended = queryClient.getQueryData(ME_QUERY_KEY) !== undefined;
    if (router.state.location.pathname !== '/login') {
      const search = { error: ended ? ('session_ended' as const) : undefined };
      await router.navigate({ to: '/login', search, replace: true });
    }
    queryClient.clear();
  };
  const queryClient = createQueryClient({
    ...(opts.retries === undefined ? {} : { retries: opts.retries }),
    onUnauthenticated: () => void leave(),
  });
  const router = createAppRouter({ entra: opts.entra, queryClient }, opts.history);
  return { queryClient, router };
}

export function App(props: {
  readonly runtime: AppRuntime;
  readonly msal: IPublicClientApplication;
}): React.JSX.Element {
  return (
    <ErrorBoundary>
      <MsalProvider instance={props.msal}>
        <QueryClientProvider client={props.runtime.queryClient}>
          <RouterProvider router={props.runtime.router} />
        </QueryClientProvider>
      </MsalProvider>
    </ErrorBoundary>
  );
}
